import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { motion } from 'framer-motion';

const Card = ({ title, description, features, imageSrc, imagePosition = 'left' }) => (
  <motion.div
    initial={{ opacity: 0, x: imagePosition === 'left' ? -50 : 50 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className="flex flex-col md:flex-row items-center bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl shadow-lg p-8 mb-10 hover:shadow-2xl hover:scale-105 transition-transform"
  >
    {imagePosition === 'left' && (
      <img src={imageSrc} alt="" className="w-24 h-24 md:mr-8 mb-4 md:mb-0" />
    )}
    <div className="flex-1 text-white/90">
      <h3 className="text-2xl md:text-3xl font-bold text-[#F3D2A5] mb-3">{title}</h3>
      <p className="mb-4">{description}</p>
      <ul className="list-disc list-inside space-y-1 mb-4 text-white/80">
        {features.map((f, idx) => (
          <li key={idx}>{f}</li>
        ))}
      </ul>
      <p className="text-white/80">
        <strong>Contact:</strong>{' '}
        <a href="mailto:info@cosmelytix.com" className="text-[#F3D2A5] underline hover:text-[#FFC971]">
          info@cosmelytix.com
        </a>
      </p>
    </div>
    {imagePosition === 'right' && (
      <img src={imageSrc} alt="" className="w-24 h-24 md:ml-8 mt-4 md:mt-0" />
    )}
  </motion.div>
);

const ForBusinessPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for your inquiry! We will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const cards = [
    {
      title: '1. Become a Product Partner',
      description: 'Showcase your cosmetic products on Cosmelytix, matching your offerings to users’ unique skin profiles.',
      features: ['Targeted product placements', 'Exposure to skin-conscious audience', 'Direct user feedback and analytics']
    },
    {
      title: '2. Brand Collaborations',
      description: 'Work directly with Cosmelytix to create co-branded campaigns and innovative product launches.',
      features: ['Collaborative marketing efforts', 'Exclusive campaign opportunities', 'Enhanced brand visibility']
    },
    {
      title: '3. Data Insights & Analytics',
      description: 'Gain valuable insights into consumer skincare preferences and behavior with our anonymized analytics tools.',
      features: ['User behavior analysis', 'Trend identification', 'Data-driven strategy optimization']
    },
    {
      title: '4. Sponsored Content & Advertising',
      description: 'Leverage our platform for targeted advertising to users interested in skincare and beauty products.',
      features: ['Custom ad placements', 'Performance tracking', 'High-engagement exposure']
    },
    {
      title: '5. Custom Collaboration or Inquiries',
      description: 'Have a unique idea? Reach out to discuss tailored partnership opportunities with Cosmelytix.',
      features: ['Bespoke collaborations', 'Consultation on innovative campaigns', 'Flexible partnership structures']
    }
  ];

  return (
    <div className="flex flex-col min-h-screen text-white bg-gradient-to-br from-[#2D1735] to-[#5F40A1]">
      <Header />

      <main className="flex-grow container mx-auto px-6 py-12 space-y-16">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center p-10 rounded-3xl bg-white/10 backdrop-blur-lg border border-white/20 shadow-lg"
        >
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-[#F3D2A5] animate-pulse">
            🤝 Partner with Cosmelytix
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto text-white/90">
            Cosmelytix is reshaping the skincare and beauty industry with data-driven insights, AI personalization, and transparent user experiences. Let's collaborate to innovate together.
          </p>
        </motion.section>

        {/* Cards Section */}
        {cards.map((card, idx) => (
          <Card
            key={idx}
            {...card}
            imageSrc="https://cosmelytix-brand-book-public.s3.us-east-1.amazonaws.com/brand_materials/Profile_icon.svg"
            imagePosition={idx % 2 === 0 ? 'left' : 'right'}
          />
        ))}

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto bg-white/20 backdrop-blur-lg border border-white/20 rounded-3xl shadow-lg p-10"
        >
          <h2 className="text-3xl font-bold text-[#F3D2A5] mb-6 text-center">Get in Touch</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {['name', 'email', 'message'].map((field) => (
              <div key={field}>
                <label htmlFor={field} className="block mb-2 font-semibold text-white/90 capitalize">
                  {field}
                </label>
                {field !== 'message' ? (
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    id={field}
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    required
                    placeholder={`Your ${field.charAt(0).toUpperCase() + field.slice(1)}`}
                    className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white/90 focus:outline-none focus:ring-2 focus:ring-[#F3D2A5] transition"
                  />
                ) : (
                  <textarea
                    id={field}
                    name={field}
                    rows="5"
                    value={formData[field]}
                    onChange={handleChange}
                    required
                    placeholder="Your Message"
                    className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/10 text-white/90 focus:outline-none focus:ring-2 focus:ring-[#F3D2A5] transition"
                  />
                )}
              </div>
            ))}
            <button
              type="submit"
              className="w-full py-3 bg-[#F3D2A5] hover:bg-[#FFC971] text-purple-900 font-bold rounded-xl transition-transform transform hover:scale-105 shadow-lg hover:shadow-2xl"
            >
              Send Message
            </button>
          </form>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ForBusinessPage;
